import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useStudents } from 'context/StudentsContext';

const StudentSearchBar = () => {
  const navigate = useNavigate();
  const { searchStudents, students } = useStudents();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const wrapperRef = useRef(null);

  const results = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return searchStudents(searchQuery).slice(0, 6);
  }, [searchQuery, searchStudents]);

  useEffect(() => {
    const onClick = (e) => {
      if (!wrapperRef.current?.contains(e.target)) {
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const goToStudent = (id) => {
    navigate(`/student-profile-management/${encodeURIComponent(id)}`);
  };

  const handleSearch = () => {
    const q = searchQuery.trim();
    if (!q) return;
    if (results.length === 1) {
      goToStudent(results[0].id);
      return;
    }
    // Multiple matches (or none) — route to the roster page. The roster's
    // own search box is the right place to browse the full result set.
    navigate('/student-profile-management');
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter') handleSearch();
  };

  const handleClear = () => setSearchQuery('');

  const showDropdown = isFocused && searchQuery.trim().length > 0;

  return (
    <div ref={wrapperRef} className="relative w-full max-w-xl mx-auto mb-8">
      <div className="relative">
        <Icon
          name="Search"
          size={18}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          placeholder={
            students.length
              ? 'Search students…'
              : 'No students yet — add one from the dashboard'
          }
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value ?? '')}
          onFocus={() => setIsFocused(true)}
          onKeyPress={handleKeyPress}
          className="w-full pl-12 pr-24 py-3 text-base bg-white border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
        />

        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              iconName="X"
              iconSize={14}
              className="text-gray-400 hover:text-gray-600 p-1"
            />
          )}
          <Button
            variant="default"
            size="sm"
            onClick={handleSearch}
            disabled={!searchQuery.trim()}
            className="px-3"
          >
            Search
          </Button>
        </div>
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 z-30 mt-2 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          {results.length === 0 ? (
            <div className="p-3 text-sm text-gray-500">
              No students match &ldquo;{searchQuery}&rdquo;.
            </div>
          ) : (
            <ul className="max-h-72 overflow-y-auto py-1">
              {results.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      goToStudent(s.id);
                      setIsFocused(false);
                    }}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-gray-50"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-700">
                      {(s.firstName?.[0] || '?').toUpperCase()}
                      {(s.lastName?.[0] || '').toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {s.firstName} {s.lastName}
                      </p>
                      <p className="truncate text-xs text-gray-500">
                        {s.id}
                        {s.grade ? ` · Grade ${s.grade}` : ''}
                      </p>
                    </div>
                    <Icon
                      name="ChevronRight"
                      size={14}
                      className="text-gray-400"
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentSearchBar;
